import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Col, Container, Row } from "reactstrap";
import { faBan, faEye } from "@fortawesome/free-solid-svg-icons";
import styles from "./style.module.scss";
import PageContainer from "@components/layout/PageContainer";
import ModelIndex, { ModelIndexHandle } from "@components/layout/ModelIndex";
import LoadingScreen from "@components/content/LoadingScreen";
import AddCreditAmountOffCanvas from "@components/layout/Offcanvas/AddCreditAmountOffCanvas";
import { ClientCreditApi, ClientCreditDataType } from "@/api/ClientCreditApi";
import { ClientApi, ClientDataType } from "@/api/ClientApi";
import { CurrencyApi, CurrencyDataType } from "@/api/CurrencyApi";
import { showAlert } from "@utils/Alert";
import { formatMoneyValue } from "@utils/Currency";
import { formatDate } from "@utils/Api";
import { OpenAddCreditAmountPubSub } from "@utils/pubsubs/PubSub";
import {
  CLIENT_CREDIT_ACTIVE_STATUS,
  CLIENT_CREDIT_CANCELLED_STATUS,
  CLIENT_CREDIT_DOCUMENT_PREFIX,
  CLIENT_CREDIT_STATUS_COLORS,
  CLIENT_CREDIT_STATUS_LABELS
} from "@utils/Constants";

const ClientCreditsIndex = () => {
  const params = useParams();
  const modelRef = useRef<ModelIndexHandle>(null);
  const { clientId } = params;
  const [client, setClient] = useState<ClientDataType>({});
  const [defaultCurrency, setDefaultCurrency] = useState<CurrencyDataType>({});
  const [credits, setCredits] = useState<ClientCreditDataType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!clientId) {
      return;
    }
    const loadPageData = async () => {
      setIsLoading(true)
      try {
        const [clientData, currencyData] = await Promise.all([
          ClientApi.get(clientId),
          CurrencyApi.getDefaultCurrency()
        ]);
        setClient(clientData);
        setDefaultCurrency(currencyData);
      } finally {
        setIsLoading(false)
      }
    }
    loadPageData();
  }, [clientId]);

  const currencyId = Number(defaultCurrency.currency_id || 1);

  const availableAmount = credits
    .filter(c => c.estado === CLIENT_CREDIT_ACTIVE_STATUS)
    .reduce((a, c) => a + (Number(c.monto || 0) - Number(c.aplicado || 0)), Number(0));

  const fetchCredits = async () => {
    if (!clientId) {
      return { items: [] };
    }
    return await ClientCreditApi.fetchAllByClient(clientId);
  }

  const cancelCredit = async (item: ClientCreditDataType) => {
    try {
      setIsLoading(true)
      await ClientCreditApi.update(`${item.id}`, { estado: CLIENT_CREDIT_CANCELLED_STATUS });
      showAlert("success", "El saldo a favor fue anulado");
      modelRef.current?.refetch();
    } catch (err) {
      if (err instanceof Error) {
        showAlert("danger", err.message);
      }
    } finally {
      setIsLoading(false)
    }
  }

  const triggerCreditAmount = () => {
    if (!defaultCurrency?.id) {
      showAlert("danger", "No fue posible determinar la moneda para el saldo a favor");
      return;
    }
    OpenAddCreditAmountPubSub.publish({
      openOffCanvas: true,
      clientId: `${clientId}`,
      clientNombre: `${client?.nombre || ""}`,
      documents: [],
      currency: defaultCurrency,
      date: ""
    })
  }

  return (
    <PageContainer
      title="Saldos a Favor del Cliente"
      backUrl="/user/clients"
      primaryActions={[
        {
          onClick: () => triggerCreditAmount(),
          actionTitle: "Agregar Monto a Favor",
          color: "success"
        }
      ]}
    >
      <Container className={styles.summary} fluid>
        <Row>
          <Col xs="4" md="3"><h5><b>Cliente:</b></h5></Col>
          <Col><h5><b>{client?.nombre}</b></h5></Col>
        </Row>
        <Row>
          <Col xs="4" md="3"><b>Saldo a favor disponible:</b></Col>
          <Col data-testid="client-credit-available">
            {formatMoneyValue(availableAmount, currencyId)}
          </Col>
        </Row>
        <hr />
      </Container>
      <ModelIndex<ClientCreditDataType>
        ref={modelRef}
        messages={{
          noResults: "Este cliente no tiene saldos a favor registrados"
        }}
        onDataFetched={(data) => setCredits(data)}
        fetchData={() => fetchCredits()}
        primaryProperties={[
          {
            key: (d) => `${CLIENT_CREDIT_STATUS_LABELS[`${d.estado}`] || d.estado}`,
            // El valor devuelto es la llave de highlightedColors, asi que se
            // normaliza a un estado conocido para no quedar sin color.
            optionalHighlighted: (val: any, data: ClientCreditDataType) => {
              return CLIENT_CREDIT_STATUS_COLORS[`${data.estado}`]
                ? `${data.estado}`
                : CLIENT_CREDIT_ACTIVE_STATUS
            },
            highlightedColors: CLIENT_CREDIT_STATUS_COLORS,
            highlighted: true
          },
          {
            key: (d) => `${CLIENT_CREDIT_DOCUMENT_PREFIX}-${d.numero}`,
            highlighted: true
          },
          {
            key: "descripcion",
            alignment: "Start"
          },
        ]}
        primaryPropertiesSizing={
          [
            { sm: "2", xs: "4" },
            { sm: "2", xs: "3" },
            { sm: "6", xs: "5" }
          ]
        }
        secondaryProperties={[
          {
            key: (data) => formatDate(data.created),
            title: "Fecha",
          },
          {
            key: (data) => formatMoneyValue(data.monto, currencyId),
            title: "Monto",
            highlighted: true
          },
          {
            key: (data) => `${data.user || "-"}`,
            title: "Usuario",
          },
          {
            key: (data) => data.recibeNumero ? `#${data.recibeNumero}` : "-",
            title: "Recibo",
          }
        ]}
        secondaryPropertiesSizing={
          [
            { sm: "2", md: "2" },
            { sm: "3", md: "3" },
            { sm: "3", md: "3" },
            { sm: "2", md: "2" }
          ]
        }
        listActions={[
          {
            isVisible: (item: ClientCreditDataType) => !!item.recibeId,
            onClick: (id: string, item: ClientCreditDataType) => {
              window.open(`/user/recibes/view/${item.recibeId}`, "_blank")
            },
            actionTitle: "Ver Recibo",
            iconProps: { icon: faEye }
          },
          {
            isVisible: (item: ClientCreditDataType) => item.estado === CLIENT_CREDIT_ACTIVE_STATUS && !!item.id,
            actionTitle: "Anular Saldo a Favor",
            iconProps: { icon: faBan },
            needConfirm: true,
            confirmProps: {
              title: "Anular Saldo a Favor",
              confirmMessage: "¿Seguro que desea anular este saldo a favor?",
              confirmBtnText: "Anular"
            },
            onClick: (id: string, item: ClientCreditDataType) => { cancelCredit(item) }
          },
        ]}
      >
      </ModelIndex>

      <AddCreditAmountOffCanvas onCreditAmountCreated={() => modelRef.current?.refetch()} />
      <LoadingScreen isLoading={isLoading} />
    </PageContainer >
  );
}

export default ClientCreditsIndex;
